import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Table, Spin, Button, Modal, Tooltip } from "antd";
import { EditOutlined, DeleteOutlined, AuditOutlined } from "@ant-design/icons"; // Icons from Ant Design
import StockTable from "../StockTable/StockTable";

// import ShippingCreationForm from "../../Forms/ShippingCreationForm/ShippingCreationForm"
const ShippingTable = ({
  history,
  loading,
  data,
  pagination,
  callReFetch,
  userData,
  dispatch,
  handleDelete,
  setSelectedNoSJ,
  stockData,
  loadingStock,
  callReFetchStock,
  onChange,
  onStockPaginationChange,
}) => {
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);

  const [selectedShipping, setSelectedShipping] = useState(null);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);

  // Separate pagination state for StockTable
  const [stockTablePagination, setStockTablePagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const EDIT_WINDOW_MS = 9999 * 60 * 60 * 1000;

  // Try createdAt; fallback to ObjectId timestamp if needed
  const getCreatedAtMs = (rec) => {
    if (rec?.createdAt) {
      const t = new Date(rec.createdAt).getTime();
      if (!Number.isNaN(t)) return t;
    }
    const id = rec?._id;
    if (typeof id === "string" && /^[a-fA-F0-9]{24}$/.test(id)) {
      return parseInt(id.slice(0, 8), 16) * 1000; // ObjectId -> seconds -> ms
    }
    return NaN;
  };

  const canModify = (rec) => {
    if (!userData?.roles?.includes("admin")) return false;
    if (rec.status !== "in delivery") return false;
    const created = getCreatedAtMs(rec);
    if (Number.isNaN(created)) return false;
    return Date.now() - created <= EDIT_WINDOW_MS;
  };

  // Update stock table pagination when stockData changes
  useEffect(() => {
    if (stockData?.pagination || stockData?.data?.pagination) {
      const paginationInfo = stockData?.pagination || stockData?.data?.pagination;
      setStockTablePagination({
        current: paginationInfo.current || 1,
        pageSize: paginationInfo.pageSize || 10,
        total: paginationInfo.total || 0,
      });
    }
  }, [stockData]);

  // Update isMobileView based on window shipping
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleOpenEditModal = async (id) => {
    const shippingToEdit = data.find((shipping) => shipping._id === id);
    if (shippingToEdit) {
      setSelectedShipping(shippingToEdit);
      setIsEditModalVisible(true);
    }

    return history.push(`/user/shipping-detail/${id}`);
  };

  // Handler that updates stock table pagination and triggers a fetch
  const handleStockDetailPaginationChange = (page, pageSize) => {
    setStockTablePagination((prev) => ({
      ...prev,
      current: page,
      pageSize,
    }));

    // Let parent know about the new page; data is paginated client-side there.
    if (onStockPaginationChange) {
      onStockPaginationChange(page, pageSize);
    }
  };

  const handleOpenDetails = async (id) => {
    const shippingToEdit = data.find((shipping) => shipping._id === id);

    if (shippingToEdit) {
      setSelectedShipping(shippingToEdit);
      setSelectedNoSJ(shippingToEdit.noSJ); // Set noSJ to fetch stock data
      setIsDetailModalVisible(true);
    }
  };

  const handleCloseEditModal = () => {
    setIsEditModalVisible(false);
    setSelectedShipping(null);
  };

const renderActionButton = (record) => {
  const showModify = canModify(record);
  return (
    <>
      <Tooltip title="Details">
        <Button
          icon={<AuditOutlined />}
          onClick={() => handleOpenDetails(record._id)}
          size="small"
          shape="circle"
          style={{ marginRight: 8 }}
        />
      </Tooltip>


      {showModify && (
        <>
          <Tooltip title="Edit ">
            <Button
              icon={<EditOutlined />}
              onClick={() => handleOpenEditModal(record._id)}
              size="small"
              shape="circle"
              style={{ marginRight: 8 }}
            />
          </Tooltip>

          <Tooltip title="Delete ">
            <Button
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record._id)}
              size="small"
              shape="circle"
              danger
            />
          </Tooltip>
        </>
      )}
    </>
  );
};

  const handleTableOnChange = (pagination, filteredInfo, sorter) => {
    if (onChange) {
      onChange(pagination, filteredInfo, sorter);
    }
  };

  const handlePaginationChange = (page, pageSize) => {
    dispatch({ type: "current", payload: page });
    dispatch({ type: "pageSize", payload: pageSize });
    callReFetch();
  };

  const statusFilter = [
    { text: "In Stock", value: "in stock" },
    { text: "In Delivery", value: "in delivery" },
  ];

  const renderFilter = (items) => {
    const filterField = {
      status: statusFilter,
    };

    return filterField[items];
  };

  const columns = [
    {
      title: "No Form",
      dataIndex: "noForm",
      key: "noForm",
      sorter: true,
    },
    {
      title: "No SJ",
      dataIndex: "noSJ",
      key: "noSJ",
      sorter: true,
    },
    {
      title: "Kirim Dari",
      dataIndex: "sender",
      key: "sender",
      sorter: true,
    },
    {
      title: "Kirim Ke",
      dataIndex: "destination",
      key: "destination",
      sorter: true,
    },
    {
      title: "Consignment Type",
      dataIndex: "consignmentType",
      key: "consignmentType",
      sorter: true,
    },
    {
      title: "Total Quantity",
      dataIndex: "totalQuantity",
      key: "totalQuantity",
      sorter: true,
    },
    {
      title: "Total Value",
      dataIndex: "totalCost",
      key: "totalCost",
      render: (price) =>
        typeof price === "number" && !isNaN(price)
          ? `Rp ${price.toLocaleString("id-ID")}`
          : "-",
      sorter: true,
    },
    {
      title: "Tanggal Kirim",
      dataIndex: "tanggal_kirim",
      key: "tanggal_kirim",
      sorter: true,
      render: (date) => {
        if (!date) return "-";
        const d = new Date(date);
        if (isNaN(d.getTime())) return "-";
        return `${d.getFullYear()}-${(d.getMonth() + 1)
          .toString()
          .padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: renderFilter("status"),
    },
    {
      title: "Keterangan",
      dataIndex: "keterangan",
      key: "keterangan",
    },
    {
      title: "Date Received",
      dataIndex: "dateReceived",
      key: "dateReceived",
      sorter: true,
      render: (date) => {
        if (!date) return "";
        const d = new Date(date);
        if (isNaN(d.getTime())) return "";
        return `${d.getFullYear()}-${(d.getMonth() + 1)
          .toString()
          .padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")} ${d
          .getHours()
          .toString()
          .padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}:${d
          .getSeconds()
          .toString()
          .padStart(2, "0")}`;
      },
    },
    {
      title: "Action",
      render: (_, record) => renderActionButton(record), // pass the full record
      width: 200,
    },
  ];

  const stockDetailData = stockData?.data || [];

  return (
    <>
      <div style={{ width: "80vw" }}>
        <Spin tip="Loading shipping data..." spinning={loading}>
          <Table
            columns={columns}
            dataSource={data}
            pagination={{
              current: pagination?.current || 1,
              pageSize: pagination?.pageSize || 10,
              total: pagination?.total || 0,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `Total ${total} items`,
              pageSizeOptions: ["10", "20", "50", "100"],
            }}
            onChange={handleTableOnChange}
            rowKey="_id"
            scroll={{ x: "max-content" }}
          />
        </Spin>

        <Modal
          title={
            <span style={{ fontWeight: 600, fontSize: 18 }}>
              Shipping Details
            </span>
          }
          open={isDetailModalVisible}
          footer={null}
          onCancel={() => setIsDetailModalVisible(false)}
          bodyStyle={{ padding: 0 }} // flush
          width={900} // wider for StockTable
          destroyOnClose
        >
          <StockTable
            history={history}
            callReFetch={callReFetchStock}
            data={stockDetailData}
            onClose={() => setIsDetailModalVisible(false)}
            onPaginationChange={handleStockDetailPaginationChange}
            loading={loadingStock}
            pagination={stockTablePagination}
            dispatch={dispatch}
            isShippingMode
          />
        </Modal>
      </div>
    </>
  );
};

ShippingTable.propTypes = {};

export default ShippingTable;
